import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useLineOfficialAccounts, useLookupProfile } from '../hooks/useResolver.js';
import type { ProfileLookupSummary } from '../types/api.js';

/**
 * @graph-node {Page}
 * @graph-stack line-user-id-resolver
 * @graph-domain Marketing, Operations
 * @graph-business line_user_id → プロフィール lookup ページ。単一 line_user_id について LINE プロフィール + arthur 連携 + aircloset 会員サマリを表示する。結果は React state にのみ保持し、永続化しない
 * @graph-connects useLineOfficialAccounts [calls] LINE 公式アカウント一覧
 * @graph-connects useLookupProfile [calls] profile mutation
 */
export const Route = createFileRoute('/profile')({
  component: ProfilePage,
});

/**
 * @graph-connects none
 */
function getSafeHttpsImageUrl(value: string | undefined): string | undefined {
  if (!value) return undefined;
  if (!URL.canParse(value)) return undefined;
  return new URL(value).protocol === 'https:' ? value : undefined;
}

/** @graph-connects none */
function ProfilePage() {
  const { t } = useTranslation();

  const accountsQuery = useLineOfficialAccounts();
  const profileMutation = useLookupProfile();

  const [accountId, setAccountId] = useState('');
  const [reason, setReason] = useState('');
  const [lineUserId, setLineUserId] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ProfileLookupSummary | null>(null);

  const accounts = useMemo(() => accountsQuery.data?.accounts ?? [], [accountsQuery.data]);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setSubmitError(null);
    setSummary(null);
    try {
      const result = await profileMutation.mutateAsync({
        lineOfficialAccountId: accountId,
        reason,
        lineUserId,
      });
      setSummary(result);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : String(err));
    }
  };

  return (
    <div className="space-y-6 p-6">
      <header>
        <h1 className="text-2xl font-bold">{t('profile.title')}</h1>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          {t('profile.description')}
        </p>
      </header>

      <section className="glass-card space-y-4 p-4">
        <form className="space-y-3" onSubmit={(e) => void handleSubmit(e)}>
          <div>
            <label className="block text-sm font-medium" htmlFor="profile-line-account">
              {t('profile.account')}
            </label>
            <select
              id="profile-line-account"
              className="mt-1 w-full rounded border px-2 py-1"
              value={accountId}
              onChange={(e) => {
                setAccountId(e.target.value);
              }}
              required
            >
              <option value="">{t('profile.selectAccount')}</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.displayName}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium" htmlFor="profile-reason">
              {t('profile.reason')}
            </label>
            <input
              id="profile-reason"
              type="text"
              className="mt-1 w-full rounded border px-2 py-1"
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
              }}
              maxLength={500}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium" htmlFor="profile-line-user-id">
              {t('profile.lineUserId')}
              <span className="ml-2 text-xs text-[var(--color-text-muted)]">
                ({t('profile.lineUserIdHint')})
              </span>
            </label>
            <input
              id="profile-line-user-id"
              type="text"
              className="mt-1 w-full rounded border px-2 py-1 font-mono"
              value={lineUserId}
              onChange={(e) => {
                setLineUserId(e.target.value);
              }}
              placeholder={t('profile.lineUserIdPlaceholder')}
              required
            />
          </div>

          {submitError ? <p className="text-sm text-[var(--color-error)]">{submitError}</p> : null}

          <button
            type="submit"
            className="rounded bg-[var(--color-accent-primary)] px-4 py-2 text-white"
            disabled={profileMutation.isPending}
          >
            {profileMutation.isPending ? t('profile.submitting') : t('profile.submit')}
          </button>
        </form>
      </section>

      {summary ? <ProfileResult summary={summary} /> : null}
    </div>
  );
}

/** @graph-connects none */
function ProfileResult({ summary }: { summary: ProfileLookupSummary }) {
  const { t } = useTranslation();
  const notProvided = t('profile.result.notProvided');
  const pictureUrl = getSafeHttpsImageUrl(summary.lineProfile?.pictureUrl);
  return (
    <section className="glass-card space-y-4 p-4">
      <h2 className="text-lg font-semibold">{t('profile.result.title')}</h2>

      {summary.errorSummary ? (
        <p className="text-sm text-[var(--color-error)]">
          {t('profile.result.errorSummary')}: {summary.errorSummary}
        </p>
      ) : null}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded border p-3">
          <h3 className="mb-2 text-sm font-semibold">{t('profile.result.lineProfile')}</h3>
          {summary.lineProfile ? (
            <div className="space-y-2 text-xs">
              {pictureUrl ? (
                <img
                  src={pictureUrl}
                  alt={summary.lineProfile.displayName}
                  className="h-16 w-16 rounded-full"
                />
              ) : null}
              <div>
                <span className="text-[var(--color-text-secondary)]">
                  {t('profile.result.displayName')}:{' '}
                </span>
                <span>{summary.lineProfile.displayName}</span>
              </div>
              <div>
                <span className="text-[var(--color-text-secondary)]">
                  {t('profile.result.language')}:{' '}
                </span>
                <span>{summary.lineProfile.language ?? notProvided}</span>
              </div>
              <div>
                <span className="text-[var(--color-text-secondary)]">
                  {t('profile.result.statusMessage')}:{' '}
                </span>
                <span>{summary.lineProfile.statusMessage ?? notProvided}</span>
              </div>
            </div>
          ) : (
            <p className="text-xs text-[var(--color-text-muted)]">
              {t('profile.result.notLinked')}
            </p>
          )}
        </div>

        <div className="rounded border p-3">
          <h3 className="mb-2 text-sm font-semibold">{t('profile.result.arthurLink')}</h3>
          {summary.arthurLink ? (
            <div className="text-xs">
              <span className="text-[var(--color-text-secondary)]">
                {t('profile.result.arthurUserId')}:{' '}
              </span>
              <span className="font-mono">{summary.arthurLink.arthurUserId}</span>
            </div>
          ) : (
            <p className="text-xs text-[var(--color-text-muted)]">
              {t('profile.result.notLinked')}
            </p>
          )}
        </div>

        <div className="rounded border p-3">
          <h3 className="mb-2 text-sm font-semibold">{t('profile.result.airclosetUser')}</h3>
          {summary.airclosetUser ? (
            <div className="space-y-1 text-xs">
              <div>
                <span className="text-[var(--color-text-secondary)]">
                  {t('profile.result.userId')}:{' '}
                </span>
                <span className="font-mono">{summary.airclosetUser.userId}</span>
              </div>
              <div>
                <span className="text-[var(--color-text-secondary)]">
                  {t('profile.result.email')}:{' '}
                </span>
                <span>{summary.airclosetUser.email ?? notProvided}</span>
              </div>
              <div>
                <span className="text-[var(--color-text-secondary)]">
                  {t('profile.result.lastName')}:{' '}
                </span>
                <span>{summary.airclosetUser.lastName ?? notProvided}</span>
              </div>
              <div>
                <span className="text-[var(--color-text-secondary)]">
                  {t('profile.result.firstName')}:{' '}
                </span>
                <span>{summary.airclosetUser.firstName ?? notProvided}</span>
              </div>
              <div>
                <span className="text-[var(--color-text-secondary)]">
                  {t('profile.result.status')}:{' '}
                </span>
                <span>{summary.airclosetUser.status ?? notProvided}</span>
              </div>
            </div>
          ) : (
            <p className="text-xs text-[var(--color-text-muted)]">
              {t('profile.result.notLinked')}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
